import React from 'react';
import { Box, Stack, Autocomplete, TextField, Button, Typography, Chip } from '@mui/material';
import { Clear as ClearIcon, FilterAlt as FilterIcon } from '@mui/icons-material';
import { UsePayerGroupFilterReturn } from '../hooks';
import { useGetCalcTypesQuery } from '../../../../../../services/ContractService';

export interface PayerGroupFilterProps {
  /** Filter state from usePayerGroupFilter hook */
  filter: UsePayerGroupFilterReturn;
  /** Label for the filter (default: "Payer Group") */
  label?: string;
  /** Whether to show the filter icon (default: true) */
  showIcon?: boolean;
}

const PayerGroupFilter: React.FC<PayerGroupFilterProps> = ({
  filter,
  label = 'Payer Group',
  showIcon = true,
}) => {
  const { value, hasFilter, setValue, clear } = filter;
  const { data: calcTypes = [], isLoading } = useGetCalcTypesQuery();

  // Map CalcTypes to options with name for display
  const options = calcTypes.map((ct) => ct.name);

  const handleChange = (_event: React.SyntheticEvent, newValue: string[]) => {
    setValue(newValue);
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
        {showIcon && <FilterIcon sx={{ color: '#64748b' }} />}
        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
          {label}:
        </Typography>
        <Autocomplete
          multiple
          size="small"
          options={options}
          value={value}
          onChange={handleChange}
          loading={isLoading}
          disableCloseOnSelect
          limitTags={3}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  key={key}
                  label={option}
                  size="small"
                  {...tagProps}
                  sx={{
                    backgroundColor: '#e0f2fe',
                    color: '#0369a1',
                    '& .MuiChip-deleteIcon': {
                      color: '#0369a1',
                      '&:hover': {
                        color: '#075985',
                      },
                    },
                  }}
                />
              );
            })
          }
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={value.length === 0 ? 'All Payer Groups' : ''}
              sx={{
                minWidth: 280,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#ffffff',
                  '& fieldset': {
                    borderColor: '#e2e8f0',
                  },
                  '&:hover fieldset': {
                    borderColor: '#3b82f6',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#3b82f6',
                  },
                },
              }}
            />
          )}
          sx={{
            minWidth: 280,
          }}
        />
        {hasFilter && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon />}
            onClick={clear}
            sx={{
              textTransform: 'none',
              borderColor: '#e2e8f0',
              color: '#64748b',
              '&:hover': {
                borderColor: '#3b82f6',
                backgroundColor: '#f8fafc',
              },
            }}
          >
            Clear
          </Button>
        )}
      </Stack>
    </Box>
  );
};

export default PayerGroupFilter;
